import { ApiCall, ApiError } from './apiCall';
import { GetTokenResponse, RevokeTokenResponse } from './Authorization';
import { TokenKeeper as OIDCTokenKeeper } from 'infrastructure/oidc/TokenKeeper';
import { Cache } from 'infrastructure/utility/Cache';

async function getAccessToken(getOIDCToken: OIDCTokenKeeper, apiCall: ApiCall, grantType: string | undefined) {
    let oidcToken: string = await getOIDCToken();
    for (; ;) {
        try {
            const json = await apiCall<GetTokenResponse>('/v2/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    'grant_type': grantType,
                    'subject_token': oidcToken,
                    'subject_token_type': 'urn:ietf:params:oauth:token-type:id_token',
                }),
            });
            return json;
        } catch (e) {
            console.debug('/v2/token API failed due to', e);
            if (e instanceof ApiError) {
                console.debug(`the failure caused by an API error: ${e.message}`);
                if (e.status == 401) {
                    console.debug(`refresh OIDC token and try again w/ ${oidcToken}`);
                    oidcToken = await getOIDCToken(oidcToken);
                    continue;
                }
            }
            throw e;
        }
    }
}

async function revokeAccessToken(token: string, apiCall: ApiCall) {
    for (; ;) {
        try {
            const json = await apiCall<RevokeTokenResponse>('/v1/revoke', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                }),
            });
            return json;
        } catch (e) {
            console.debug('/v1/revoke API failed due to', e);
            if (e instanceof ApiError) {
                console.debug(`the failure caused by an API error: ${e.message}`);
            }
            throw e;
        }
    }
}

export type TokenInfo = {
    token: string;
    expiresAt: Date;
}

export class TokenKeeper {
    readonly getOIDCToken: OIDCTokenKeeper;
    readonly apiCall: ApiCall;
    readonly grantType: string | undefined;
    readonly cache = new Cache<TokenInfo>();

    constructor(getOIDCToken: OIDCTokenKeeper, apiCall: ApiCall, grantType: string | undefined) {
        this.getOIDCToken = getOIDCToken;
        this.apiCall = apiCall;
        this.grantType = grantType;
    }

    getCurrentInfo() {
        return this.cache.currentValue;
    }

    async get() {
        this.retakeToken({ conservative: true });
        return await this.cache.get();
    }

    async retake() {
        this.retakeToken({ conservative: false });
        return await this.cache.get();
    }

    async revoke() {
        try {
            const tokenInfo = await this.cache.get();
            await revokeAccessToken(tokenInfo.token, this.apiCall);
        } catch (e) {
            console.error(e);
        }
    }
    private listeners = new Set<(accessToken: string, expiresAt: Date) => void>();

    addListener(callback: (accessToken: string, expiresAt: Date) => void): void {
        this.listeners.add(callback);
    }

    removeListener(callback: (accessToken: string, expiresAt: Date) => void): void {
        this.listeners.delete(callback);
    }

    private retakeToken(options: { conservative: boolean }) {
        this.cache.update(() => this.obtainToken(), options);
    }

    private async obtainToken() {
        const newToken = await getAccessToken(this.getOIDCToken, this.apiCall, this.grantType);
        const timeout = parseFloat(newToken.expires_in) * 1000.0;
        const expiresAt = new Date(Date.now() + timeout);
        [...this.listeners].forEach(listener => listener(newToken.access_token, expiresAt));
        setTimeout(() => {
            this.retakeToken({ conservative: false });
        }, timeout / 2);

        return { token: newToken.access_token, expiresAt };
    }
}

export function createTokenKeeper(getOIDCToken: OIDCTokenKeeper, apiCall: ApiCall, grantType: string | undefined): TokenKeeper {
    return new TokenKeeper(getOIDCToken, apiCall, grantType);
}
